/**
 * Log levels per service, set in Administration → System status. The setting
 * (system_settings `log_levels`) maps a target to a level; no entry means
 * "Default": the level the service started with (its LOG_LEVEL).
 *
 * Targets: `api`, `workflow`, `graphics-renderer`, one `media-worker:<role>`
 * per worker role, and `agent:<id>` for each storage agent.
 */

export const LOG_LEVELS = ['trace', 'debug', 'info', 'warn', 'error', 'fatal'] as const;
export type LogLevel = (typeof LOG_LEVELS)[number];

export const LOG_LEVEL_NUMBER: Record<LogLevel, number> = { trace: 10, debug: 20, info: 30, warn: 40, error: 50, fatal: 60 };

export const isLogLevel = (v: unknown): v is LogLevel => typeof v === 'string' && (LOG_LEVELS as readonly string[]).includes(v);

/** The services that can be given a level, besides storage agents (`agent:<id>`). */
export const LOG_SERVICE_TARGETS = [
  { target: 'api', label: 'API', service: 'prism-api' },
  { target: 'workflow', label: 'Workflow service', service: 'prism-workflow' },
  { target: 'graphics-renderer', label: 'Graphics renderer', service: 'prism-graphics-renderer' },
  { target: 'media-worker:process', label: 'Media worker: processing', service: 'prism-media-worker' },
  { target: 'media-worker:transcode', label: 'Media worker: transcoding', service: 'prism-media-worker' },
  { target: 'media-worker:transfer', label: 'Media worker: transfers', service: 'prism-media-worker' },
  { target: 'media-worker:live', label: 'Media worker: live', service: 'prism-media-worker' },
] as const;

/** Whether `target` can be given a level. */
export const isLogTarget = (t: string): boolean => LOG_SERVICE_TARGETS.some((x) => x.target === t) || /^agent:[0-9a-f-]{36}$/.test(t);

/** The most verbose (lowest) of the given levels, or null when none is set. */
export function mostVerbose(levels: Array<string | null | undefined>): LogLevel | null {
  let best: LogLevel | null = null;
  for (const l of levels) if (isLogLevel(l) && (!best || LOG_LEVEL_NUMBER[l] < LOG_LEVEL_NUMBER[best])) best = l;
  return best;
}

/** The part of a pino logger the level controller needs. */
export interface LevelLogger { level: string; info(obj: Record<string, unknown>, msg: string): void }

/**
 * Applies a level from the control plane to a logger: a valid level is used,
 * anything else (null: "Default") restores `startup`. Returns whether the
 * level changed. The change is logged at a level that shows it.
 */
export function applyLogLevel(logger: LevelLogger, startup: string, requested: unknown): boolean {
  const next = isLogLevel(requested) ? requested : startup;
  const before = logger.level;
  if (next === before) return false;
  const quieter = (LOG_LEVEL_NUMBER[next as LogLevel] ?? 30) > (LOG_LEVEL_NUMBER[before as LogLevel] ?? 30);
  if (quieter) logger.info({ from: before, to: next }, 'log level changed');
  logger.level = next;
  if (!quieter) logger.info({ from: before, to: next }, 'log level changed');
  return true;
}
